import { SubscriptionStateEnum } from "@/features/billing/types";
import { HttpStatusCode, Method } from "axios";

export type TUser = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber: null;
  lastLogin: string;
  profile?: string;
  userOrganizations?: {
    id: string;
    createdAt: string;
    updatedAt: string;
    deletedAt: null;
    userId: string;
    organizationId: string;
    roleId: string;
    roleName: string;
    employeeId: string;
  }[];
};

export type TUserParamsType = {
  page: number;
  limit: number;
  orderBy?: "NAME" | "EMAIL";
  orderDirection?: "ASC" | "DESC";
  search?: string;
  organizationId?: string;
  roleIds?: string[];
  userState?: "ACTIVE" | "INACTIVE";
};

export type TLogActivity = {
  id: string;
  actorId: string;
  actorFullName: string;
  actorRole: string;
  eventCategory: string;
  eventName: string;
  eventDescription: string;
  ip: string;
  success: boolean;
  time: Date;
  companyId: string;
  metaAsJson: string;
  meta: {
    method: Method;
    url: string;
    response: {
      statusCode: HttpStatusCode | number;
    };
    relatedEntities?: any;
  };
};

export enum EUserRole {
  OWNER = "OWNER",
  SUPER_ADMIN = "SUPER_ADMIN",
  IT_ADMIN = "IT_ADMIN",
  HR_ADMIN = "HR_ADMIN",
  TRIO_ADMIN = "TRIO_ADMIN",
  USER = "USER",
}

export enum EEventCategory {
  AUTHENTICATION = "AUTHENTICATION",
  SETTING = "SETTING",
  INTEGRATION = "INTEGRATION",
  DEPARTMENTS = "GROUP",
  EMPLOYEE = "USER",
  SOFTWARE = "SOFTWARE",
  DEVICE = "DEVICE",
  DEVICE_GROUP = "DEVICE_GROUP",
}

export type TGetActivityLogsParams = {
  actorId?: string;
  actorRole?: EUserRole;
  eventCategory?: EEventCategory[];
  eventDescription?: string;
  eventName?: string;
  ip?: string;
  success?: boolean;
  companyId?: string;
  fenceIds?: string;
  fromDate?: string;
  toDate?: string;
  page: number;
  limit: number;
  type?: "CHECK_IN" | "CHECK_OUT";
  end?: string;
  start?: string;
  orderBy?:
    | "actorId"
    | "actorRole"
    | "eventCategory"
    | "eventName"
    | "ip"
    | "success"
    | "companyId"
    | "time";
  orderDirection?: ESort;
  deviceGroupId?: string;
};

export type TOrganizationParamsType = {
  page: number;
  limit: number;
  orderBy?: "name";
  orderDirection?: "ASC" | "DESC";
  search?: string;
  userId?: string;
  subscriptionStatus?: SubscriptionStateEnum[];
};

export type TMeta = {
  itemsCount: number;
  totalItems: number;
  itemsPerPage: number;
  totalPages: number;
  currentPage: number;
  nextPage: number;
  prevPage: number;
};

export type TOrganizationBillingDetails = {
  status: string;
  devicesCount: number;
  plan: string;
  period: string;
  osCounts: {
    os: string;
    osCount: number;
  }[];
};

/** Latest subscription row joined on the org list query (when present). */
export type TOrganizationListSubscription = {
  id: string;
  stripeSubscriptionId?: string;
  expiresIn?: string | null;
  startsIn?: string | null;
  status?: string;
  devicesCount?: number;
  organizationId?: string;
  companyId?: string;
  priceId?: string | null;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string | null;
};

export type TOrganizationListOwner = {
  id: string;
  email?: string;
  firstName?: string;
  lastName?: string | null;
  profile?: string | null;
  phoneNumber?: string | null;
  lastLogin?: string | null;
  isOwner?: boolean;
  status?: string;
  companyId?: string;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string | null;
};

export type TOrganization = {
  id: string;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
  name: string;
  displayName?: string | null;
  region: string;
  logo?: string | null;
  email?: string | null;
  description?: string | null;
  country: string;
  industry?: string | null;
  ownerId: string;
  companyId: string;
  url: string;
  mspCompanyId: string;
  createdById?: string | null;
  owner?: TOrganizationListOwner;
  userCount?: number;
  billingDetails?: TOrganizationBillingDetails;
  subscriptions?: TOrganizationListSubscription[];
};

export type TOrganizationDetail = {
  id: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: string;
  name: string;
  displayName: string;
  region: string;
  logo: null | string | File;
  email: string;
  description: null | string;
  country: string;
  industry: string;
  ownerId: string;
  companyId: string;
  url: string;
  mspCompanyId: string;
};

export type TOrganizationDevicesParams = {
  page: number;
  limit: number;
  search?: number;
};

export type TDevice = {
  id: string;
  name: string;
  serialNumber: string;
  ownershipType: string;
  ramSize: number;
  platform: string;
  os: string;
  osName: string;
  osVersion: string;
  createdAt: string;
  hardwareId: string;
  hardsSize: number;
  metadata: string;
  locationTrackingStatus: string;
  enrollType: string;
  disconnectedAt: string;
  enrollStatus: string;
  upTime: number;
  model: string;
  isAssigned: boolean;
  employee?: GetDeviceListResponse_DeviceBasic_EmployeeDevice | null;
  isOnline: boolean;
};

export interface GetDeviceListResponse_DeviceBasic_EmployeeDevice_DepartmentDevice {
  id: string;

  name: string;
}

export interface GetDeviceListResponse_DeviceBasic_EmployeeDevice {
  id: string;
  firstName: string;
  lastName: string;
  profile: string;
  email?: string;
  department?: GetDeviceListResponse_DeviceBasic_EmployeeDevice_DepartmentDevice;
}
export type OSType =
  | "IPADOS"
  | "IOS"
  | "TVOS"
  | "MACOS"
  | "ANDROID"
  | "LINUX"
  | "WINDOWS";

export type TSort = "ASC" | "DESC";

export type TGetDevicesParams = {
  osTypes?: OSType[];
  search?: string;
  enrollmentDate?: string;
  lastSeenDate?: string;
  page: number;
  limit: number;
  orderBy?: "name" | "createdAt" | "os" | "disconnectedAt";
  orderDirection?: TSort;
  employeeIds?: string;
  departmentIds?: string;
  toEnrolmentDate?: string | null;
  fromEnrolmentDate?: string | null;
  fromLastSeenDate?: string | null;
  toLastSeenDate?: string | null;
  serialNumberSearch?: string | null;
  status?: string[];
  excludeProfileId?: string;
  enrollTypes?: string[];
  returnGeofences?: boolean;
  ownershipTypes?: string[];
};
export enum EOSType {
  ALL = "All",
  ANDROID = "ANDROID",
  IOS = "IOS",
  IPADOS = "IPADOS",
  MACOS = "MACOS",
  WINDOWS = "WINDOWS",
  TVOS = "TVOS",
}
export enum ESort {
  ASC = "ASC",
  DESC = "DESC",
}
